import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import CustomerRouteGate from './components/layout/CustomerRouteGate';
import ScrollToTop from './components/ScrollToTop';
import { AuthProvider } from './contexts/AuthContext';
import { queryClient } from './lib/queryClient';
import { usePageTracking } from './hooks/usePageTracking';

// Lazy-load all page components
const HomePage = lazy(() => import('./pages/HomePage'));
const CatalogPage = lazy(() => import('./pages/CatalogPage'));
const OrnamentDetailPage = lazy(() => import('./pages/OrnamentDetailPage'));
const PricingPage = lazy(() => import('./pages/PricingPage'));
const HowItWorksPage = lazy(() => import('./pages/HowItWorksPage'));
const OrderPage = lazy(() => import('./pages/OrderPage'));
const OrderConfirmationPage = lazy(
  () => import('./pages/OrderConfirmationPage'),
);
const PaymentPage = lazy(() => import('./pages/PaymentPage'));
const OrderStatusPage = lazy(() => import('./pages/OrderStatusPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const OccasionsPage = lazy(() => import('./pages/OccasionsPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));
const AcceptInvitePage = lazy(() => import('./pages/admin/AcceptInvitePage'));

// Admin pages - separate chunk
const AdminDashboardPage = lazy(
  () => import('./pages/admin/AdminDashboardPage'),
);
const AdminOrdersPage = lazy(() => import('./pages/admin/AdminOrdersPage'));
const AdminInventoryPage = lazy(
  () => import('./pages/admin/AdminInventoryPage'),
);
const AdminOrnamentFormPage = lazy(
  () => import('./pages/admin/AdminOrnamentFormPage'),
);
const AdminCalendarPage = lazy(() => import('./pages/admin/AdminCalendarPage'));
const AdminOrderDetailPage = lazy(
  () => import('./pages/admin/AdminOrderDetailPage'),
);
const AdminSettingsPage = lazy(() => import('./pages/admin/AdminSettingsPage'));
const AdminPrivacyPage = lazy(() => import('./pages/admin/AdminPrivacyPage'));
const AdminMessagesPage = lazy(() => import('./pages/admin/AdminMessagesPage'));
const AdminDeliveriesPage = lazy(
  () => import('./pages/admin/AdminDeliveriesPage'),
);
const AdminGalleryModerationPage = lazy(
  () => import('./pages/admin/AdminGalleryModerationPage'),
);
const AdminDriversPage = lazy(() => import('./pages/admin/AdminDriversPage'));
const AdminPhrasesPage = lazy(() => import('./pages/admin/AdminPhrasesPage'));
const AdminOccasionsPage = lazy(
  () => import('./pages/admin/AdminOccasionsPage'),
);
const AdminOccasionFormPage = lazy(
  () => import('./pages/admin/AdminOccasionFormPage'),
);
const AdminCouponsPage = lazy(() => import('./pages/admin/AdminCouponsPage'));
const AdminCouponFormPage = lazy(
  () => import('./pages/admin/AdminCouponFormPage'),
);
const AdminCustomersPage = lazy(
  () => import('./pages/admin/AdminCustomersPage'),
);
const AdminCustomerDetailPage = lazy(
  () => import('./pages/admin/AdminCustomerDetailPage'),
);
const AdminActivityLogPage = lazy(
  () => import('./pages/admin/AdminActivityLogPage'),
);
const AdminNotificationHistoryPage = lazy(
  () => import('./pages/admin/AdminNotificationHistoryPage'),
);
const AdminUsersPage = lazy(() => import('./pages/admin/AdminUsersPage'));
const AdminProfilePage = lazy(() => import('./pages/admin/AdminProfilePage'));
const ProtectedAdminRoute = lazy(
  () => import('./components/admin/ProtectedAdminRoute'),
);

// Driver pages - separate chunk
const DriverAssignmentsPage = lazy(
  () => import('./pages/driver/DriverAssignmentsPage'),
);
const DriverProfilePage = lazy(
  () => import('./pages/driver/DriverProfilePage'),
);
const ProtectedDriverRoute = lazy(
  () => import('./components/driver/ProtectedDriverRoute'),
);

function PageTracker() {
  usePageTracking();
  return null;
}

export default function App() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <PageTracker />
      <Suspense
        fallback={
          <div className="flex min-h-screen items-center justify-center bg-surface-page">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-200 border-t-primary-600" />
          </div>
        }
      >
        {/*
          A single QueryClientProvider + AuthProvider wraps every route group so
          the in-memory Cognito session is shared across them. This is what lets
          a staff member authenticated under /admin carry their session into the
          customer routes and preview the storefront while the store is Closed
          (#38). The session is still in-memory only (lost on a full page
          reload), so anonymous/cold loads correctly see the holding page.
        */}
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <Routes>
              {/* Public routes */}
              <Route element={<CustomerRouteGate />}>
                <Route path="/" element={<HomePage />} />
                <Route path="/ornaments" element={<CatalogPage />} />
                <Route
                  path="/ornament/:name"
                  element={<OrnamentDetailPage />}
                />
                <Route path="/pricing" element={<PricingPage />} />
                <Route path="/how-it-works" element={<HowItWorksPage />} />
                <Route path="/order" element={<OrderPage />} />
                <Route
                  path="/order/confirmation/:id"
                  element={<OrderConfirmationPage />}
                />
                <Route path="/pay/:token" element={<PaymentPage />} />
                <Route
                  path="/order/status/:token"
                  element={<OrderStatusPage />}
                />
                <Route path="/occasions" element={<OccasionsPage />} />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="*" element={<NotFoundPage />} />
              </Route>

              {/* Accept invitation - public, standalone (no layout wrapper) */}
              <Route path="/accept-invite" element={<AcceptInvitePage />} />

              {/* Admin routes - protected by auth */}
              <Route path="/admin" element={<ProtectedAdminRoute />}>
                <Route index element={<AdminDashboardPage />} />
                <Route path="orders" element={<AdminOrdersPage />} />
                <Route path="orders/:id" element={<AdminOrderDetailPage />} />
                <Route path="messages" element={<AdminMessagesPage />} />
                <Route path="deliveries" element={<AdminDeliveriesPage />} />
                <Route path="inventory" element={<AdminInventoryPage />} />
                <Route
                  path="inventory/new"
                  element={<AdminOrnamentFormPage />}
                />
                <Route
                  path="inventory/:id/edit"
                  element={<AdminOrnamentFormPage />}
                />
                <Route path="calendar" element={<AdminCalendarPage />} />
                <Route path="settings" element={<AdminSettingsPage />} />
                <Route path="settings/privacy" element={<AdminPrivacyPage />} />
                <Route
                  path="gallery/moderation"
                  element={<AdminGalleryModerationPage />}
                />
                <Route path="drivers" element={<AdminDriversPage />} />
                <Route path="customers" element={<AdminCustomersPage />} />
                <Route
                  path="customers/:email"
                  element={<AdminCustomerDetailPage />}
                />
                <Route path="users" element={<AdminUsersPage />} />
                <Route path="profile" element={<AdminProfilePage />} />
                <Route path="phrases" element={<AdminPhrasesPage />} />
                <Route path="occasions" element={<AdminOccasionsPage />} />
                <Route
                  path="occasions/new"
                  element={<AdminOccasionFormPage />}
                />
                <Route
                  path="occasions/:id/edit"
                  element={<AdminOccasionFormPage />}
                />
                <Route path="coupons" element={<AdminCouponsPage />} />
                <Route path="coupons/new" element={<AdminCouponFormPage />} />
                <Route
                  path="coupons/:id/edit"
                  element={<AdminCouponFormPage />}
                />
                <Route path="activity-log" element={<AdminActivityLogPage />} />
                <Route
                  path="notifications/history"
                  element={<AdminNotificationHistoryPage />}
                />
              </Route>

              {/* Driver routes - protected by auth, requires driver role */}
              <Route path="/driver" element={<ProtectedDriverRoute />}>
                <Route index element={<Navigate to="assignments" replace />} />
                <Route path="assignments" element={<DriverAssignmentsPage />} />
                <Route path="profile" element={<DriverProfilePage />} />
              </Route>
            </Routes>
          </AuthProvider>
        </QueryClientProvider>
      </Suspense>
    </BrowserRouter>
  );
}
